import { FunctionComponent ,useEffect,useState} from "react";
import { useForm, SubmitHandler, FormProvider } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';
import InputField from "../../../components/InputField/InputField";
import ButtonStyle from "../../../components/ButtonStyle/ButtonStyle";
import "./SchoolAdminLogin.scss";
import { messages, PATH_ADDRESSES, patterns } from "../../../utils/constants";
import { useAppDispatch } from '../../../redux/app/hooks';
import { login, logout, setLoginPage, setSchoolType, setTeacherMainRoute } from '../../../redux/slices/authSlice';
import { useLocation, useNavigate } from "react-router-dom";
import {signIn} from 'aws-amplify/auth'
import ToasterWithMessage from "../../../components/toaster";
import { getScreenDimensions, handleInputWithoutSpaces } from "../../../utils/helper";
import { trackButtonClick } from "../../../firebase/analyticsHelper";
import { ButtonClickEvents } from "../../../firebase/constants/events";


//validation
const schema = (loginpage: string) => yup.object().shape({
  //username validation
  username: ["ptaLogin", "participantLogin"].includes(loginpage) ?
  yup.string().required('Email is required')
    .trim()
    .test('is-valid-email', messages.validEmail, function (value) {
        const { path, createError } = this;
        if (value && value.length > 0) {
            return patterns.email.test(value) || createError({ path, message: messages.validEmail });
        }
        return true;
    }) : yup
    .string()
    .strict()
    .trim(messages.invalidUsername)
    .required(messages.username),
  //password validation min, max...
  password: yup
    .string()
    .strict()
    .trim(messages.invalidPassword)
    .required(messages.password),
});

interface IFormInput {
  username: string;
  password: string;
}

interface PropsType{
  title: string;
  loginpage: string;
  setSignupSuccess?: (val: boolean) => void;
}

const SchoolAdminLogin: FunctionComponent<PropsType> = ({title, loginpage, setSignupSuccess}) => {
  const {error} = ToasterWithMessage()
  const location = useLocation();
  const [disableButton, setDisableButton] = useState(false);
  const navigate = useNavigate();
  const methods = useForm<IFormInput>({
    resolver: yupResolver(schema(loginpage))
  });
  const { handleSubmit, setFocus, formState: {errors}, reset } = methods;
  const dispatch = useAppDispatch();
  const [windowWidth, setWindowWidth] = useState(window.innerWidth);
  const { isMobileScreen } = getScreenDimensions();
  

  const onSubmit: SubmitHandler<IFormInput> = data => {
    trackButtonClick(ButtonClickEvents?.LOGIN_BUTTON_CLICK, {
      description : "User clicked the login button"
    })
    if(errors?.username || errors?.password){
      setDisableButton(false);
    }
    else{
      setDisableButton(true);
    }
    //onSubmit Store the data in redux and navigate to admin-dashboard
    CognitoSignIn({username: data?.username,password: data?.password})
  };

  // hit the cognito to get the Token
  const CognitoSignIn = async (data: IFormInput) => {
    try {
      await signIn({username: data.username,password: data.password})
     .then(async (datas)=>{
        if(datas?.isSignedIn){
          const accessToken : any =localStorage.getItem(Object.keys(localStorage).find((key) => key.includes('accessToken')) as string) || null;
          const token = JSON.parse(window.atob(accessToken.split('.')[1]));
          const role = token['cognito:groups'][0];
          
          if (loginpage === "facilitatorLogin") {
            if (role === "facilitator" || role === "schooladmin") {
              dispatch(setLoginPage({ loginPage: 'facilitator' }));
              dispatch(login({ role: role ? role : null }));

              localStorage.removeItem('generalCode');
              navigate('/facilitator-workshop');
            } else {
              error("Provide facilitator credentials");
              dispatch(logout());
              setDisableButton(false);
            }
          } else if (loginpage === "participantLogin") {
            if (role === "participant") {
              dispatch(setLoginPage({ loginPage: 'participant' }));
              dispatch(login({ role: role ? role : null }));
              localStorage.removeItem('generalCode');
              navigate('/participant-workshop');
            } else {
              error("Provide participant credentials");
              dispatch(logout());
              setDisableButton(false);
            }
          } else if(loginpage === 'adminLogin'){
            if(role === 'schooladmin'){
              dispatch(setLoginPage({loginPage: 'schooladmin'}));
              dispatch(login({role: role ? role : null}));
              localStorage.removeItem('generalCode');
              navigate(PATH_ADDRESSES?.ADMIN_DASHBOARD_PATH);
            }else{
              error("Provide admin credentials");
              dispatch(logout());
              setDisableButton(false);
            }
          }else if(loginpage === 'teacherLogin' || loginpage === 'virtualTeacherLogin'){
            if(role === 'teacher'){
              dispatch(setLoginPage({loginPage: 'teacher'}));
              dispatch(login({role: role ? role : null}));
              dispatch(setSchoolType({schoolType: loginpage === 'virtualTeacherLogin' ? 'VIRTUAL' : ''}));
              dispatch(setTeacherMainRoute({
                teacherMainRoute: loginpage === 'virtualTeacherLogin' 
                                  ? '/virtual-school/teacher-select-class?isKaton=virtualSchool' 
                                  : '/teacher-select-class'
              }));
              navigate(loginpage === 'virtualTeacherLogin' ? '/virtual-school/teacher-select-class?isKaton=virtualSchool' : '/teacher-select-class');
            }else {
              setDisableButton(false);
              error("Provide teacher credentials");
              dispatch(logout());
            }
           
          }else if(loginpage === 'studentLogin' || loginpage === 'virtualStudentLogin'){
            if(role === 'student'){
              dispatch(setLoginPage({loginPage: 'student'}));
              dispatch(login({role: role ? role : null}));
              localStorage.removeItem('generalCode');
              if(setSignupSuccess){
                setSignupSuccess(true);
              }
              if(loginpage === 'virtualStudentLogin'){
                dispatch(setSchoolType({schoolType: 'VIRTUAL'}));
                navigate(PATH_ADDRESSES?.STUDENT_CLASSROOM_PATH);
              }else{
                dispatch(setTeacherMainRoute({teacherMainRoute: '/student-verifyCode'}));
                navigate('/student-verifyCode');
              }
            }else {
              setDisableButton(false);
              error("Provide student credentials");
              dispatch(logout());
            }
          }else if(loginpage === 'ptaLogin'){
            if(role === 'pta'){
              localStorage.removeItem('isGuest');
              dispatch(setLoginPage({loginPage: 'pta'}));
              dispatch(login({role: role ? role : null}));
              navigate('/pta-meetings', {
                state: location?.state
              });
              localStorage.setItem("isVerify", "true");
              if(setSignupSuccess){
                setSignupSuccess(true);
              }
            }else {
              setDisableButton(false);
              error("Provide PTA credentials");
              dispatch(logout());
            }
          }else {
            dispatch(logout());
          }
        }
     })
     .catch((err)=>{
        error(err?.message);
        setDisableButton(false);
        dispatch(logout());
      })
   } catch (err) {
    error("Something went wrong!");
    setDisableButton(false);
    dispatch(logout());
   }
  }

  //focus on email 
  useEffect(() => {
    setFocus('username');
  }, [setFocus]);

  useEffect(() => {
      dispatch(logout());
      reset({
        username: '',
        password: ''
      });
  },[location])

  useEffect(() => {
    const handleResize = () => {
      setWindowWidth(window.innerWidth);
    };

    window.addEventListener("resize", handleResize);
    return () => {
      window.removeEventListener("resize", handleResize);
    };
  }, []);

  return (
    <FormProvider {...methods}>
      <div className="adminSignInForm">
        <label className="logInAs">{isMobileScreen ? "Login" : title}</label>
        <form autoComplete="off" className="signInForm" onSubmit={handleSubmit(onSubmit)} noValidate>
        <InputField 
            label={["ptaLogin", "participantLogin"].includes(loginpage) ? "Email" : "Username"} 
            name="username" 
            type="text" 
            size="full" 
            onInput={handleInputWithoutSpaces} 
        />
        <div className="passwordContainer">
          <InputField label="Password" name="password" type="password" size="full" onInput={handleInputWithoutSpaces} />
          {(location.pathname === '/student-login') && 
            <div 
              className='forgotpassword' 
              onClick={()=>{
                trackButtonClick(ButtonClickEvents?.FORGOT_PASSWORD_TEXT_CLICK, { description: "User clicked the forgot password button" }); 
                navigate('/student-forgot-password')}}
            >
              Forgot password?
            </div>
          }
        </div>
        <div className="cta" style={{justifyContent: (location.pathname === '/student-login' || location?.pathname?.includes("virtual-student-login")) ? 'center' : 'flex-end'}}>
        <ButtonStyle disableButton={disableButton} text="Login" type="submit" width={windowWidth > 768 ? "154px" : "350px"} />
        </div>
        {(isMobileScreen && loginpage === "studentLogin") && <div className='ctaLogin'>
            <div className='textStyle'>I don’t have an account</div>
            <div className='logInCtaStyle' onClick={() => navigate('/student-signup')}>Sign up now</div>
        </div>}
        </form>
      </div>
    </FormProvider>
  );
};

export default SchoolAdminLogin;
